import { getAllBookingsFromFirestore } from './firestoreBooking';

/**
 * Get unavailable beds for a specific date range
 * @param {string} checkInDate - Check-in date in format 'YYYY-MM-DD' or 'MMM DD, YYYY'
 * @param {string} checkOutDate - Check-out date in format 'YYYY-MM-DD' or 'MMM DD, YYYY'
 * @returns {Promise<string[]>} Array of unavailable bed IDs
 */
export const getUnavailableBeds = async (checkInDate, checkOutDate) => {
  try {
    // Get all bookings from Firestore
    const allBookings = await getAllBookingsFromFirestore();
    
    // Convert input dates to Date objects for comparison
    const requestedCheckIn = parseDate(checkInDate);
    const requestedCheckOut = parseDate(checkOutDate);
    
    if (!requestedCheckIn || !requestedCheckOut) {
      console.warn('Invalid date format provided to getUnavailableBeds');
      return [];
    }
    
    const unavailableBeds = new Set();
    
    // Check each booking for date conflicts
    allBookings.forEach(booking => {
      try {
        // Parse booking dates
        const bookingCheckIn = parseDate(booking.checkIn || booking.start);
        const bookingCheckOut = parseDate(booking.checkOut || booking.end);
        
        if (!bookingCheckIn || !bookingCheckOut) {
          return; // Skip invalid booking dates
        }
        
        // Check if dates overlap
        if (datesOverlap(requestedCheckIn, requestedCheckOut, bookingCheckIn, bookingCheckOut)) {
          // Add all beds from this booking to unavailable list
          if (booking.selectedBeds && Array.isArray(booking.selectedBeds)) {
            booking.selectedBeds.forEach(bed => {
              const bedId = typeof bed === 'object' ? (bed.id || bed.number || bed.display) : bed;
              if (bedId) {
                unavailableBeds.add(bedId.toString());
              }
            });
          }
          
          // Also check bedNumbers field as fallback
          if (booking.bedNumbers && Array.isArray(booking.bedNumbers)) {
            booking.bedNumbers.forEach(bedId => {
              if (bedId) {
                unavailableBeds.add(bedId.toString());
              }
            });
          }
        }
      } catch (error) {
        console.warn('Error processing booking:', booking.id, error);
      }
    });
    
    return Array.from(unavailableBeds);
  } catch (error) {
    console.error('Error fetching bed availability:', error);
    return []; // Return empty array on error to avoid blocking the UI
  }
};

/**
 * Parse various date formats into Date object
 * @param {string} dateString - Date in various formats
 * @returns {Date|null} Parsed date or null if invalid
 */
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return new Date(dateString + 'T00:00:00');
    }
    
    // Handle other formats like "Aug 15, 2023"
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    
    // Set to start of day for consistent comparison
    date.setHours(0, 0, 0, 0);
    return date;
  } catch (error) {
    console.warn('Error parsing date:', dateString, error);
    return null;
  }
};

/**
 * Check if two date ranges overlap
 * @param {Date} start1 - Start of first range
 * @param {Date} end1 - End of first range
 * @param {Date} start2 - Start of second range
 * @param {Date} end2 - End of second range
 * @returns {boolean} True if ranges overlap
 */
const datesOverlap = (start1, end1, start2, end2) => {
  // Convert to timestamps for easier comparison
  const s1 = start1.getTime();
  const e1 = end1.getTime();
  const s2 = start2.getTime();
  const e2 = end2.getTime();
  
  // Check if ranges overlap
  // Range 1: [s1, e1], Range 2: [s2, e2]
  // They overlap if: s1 < e2 && s2 < e1
  return s1 < e2 && s2 < e1;
};

/**
 * Check if specific beds are available for given dates (excluding current booking)
 * @param {string[]} bedIds - Array of bed IDs to check
 * @param {string} checkInDate - Check-in date
 * @param {string} checkOutDate - Check-out date
 * @param {string} excludeBookingId - Booking ID to exclude from check (for updates)
 * @returns {Promise<{available: string[], unavailable: string[]}>} Available and unavailable beds
 */
export const checkBedsAvailability = async (bedIds, checkInDate, checkOutDate, excludeBookingId = null) => {
  try {
    const unavailableBeds = await getUnavailableBeds(checkInDate, checkOutDate);
    
    // Filter out the excluded booking if provided
    let filteredUnavailable = unavailableBeds;
    if (excludeBookingId) {
      const allBookings = await getAllBookingsFromFirestore();
      const excludedBooking = allBookings.find(b => b.id === excludeBookingId);
      if (excludedBooking && excludedBooking.selectedBeds) {
        const excludedBedIds = excludedBooking.selectedBeds.map(bed => 
          typeof bed === 'object' ? (bed.id || bed.number || bed.display) : bed
        );
        filteredUnavailable = unavailableBeds.filter(bedId => !excludedBedIds.includes(bedId));
      }
    }
    
    const available = [];
    const unavailable = [];
    
    bedIds.forEach(bedId => {
      if (filteredUnavailable.includes(bedId)) {
        unavailable.push(bedId);
      } else {
        available.push(bedId);
      }
    });
    
    return { available, unavailable };
  } catch (error) {
    console.error('Error checking beds availability:', error);
    // Return all beds as available on error to avoid blocking
    return { available: bedIds, unavailable: [] };
  }
};

/**
 * Validate bed selection before booking
 * @param {string[]} selectedBedIds - Array of selected bed IDs
 * @param {string} checkInDate - Check-in date
 * @param {string} checkOutDate - Check-out date
 * @returns {Promise<{valid: boolean, conflicts: string[], message: string}>} Validation result
 */
export const validateBedSelection = async (selectedBedIds, checkInDate, checkOutDate) => {
  try {
    if (!selectedBedIds || selectedBedIds.length === 0) {
      return {
        valid: false,
        conflicts: [],
        message: 'Please select at least one bed'
      };
    }
    
    if (!checkInDate || !checkOutDate) {
      return {
        valid: false,
        conflicts: [],
        message: 'Please select check-in and check-out dates'
      };
    }
    
    const { available, unavailable } = await checkBedsAvailability(selectedBedIds, checkInDate, checkOutDate);
    
    if (unavailable.length > 0) {
      return {
        valid: false,
        conflicts: unavailable,
        message: `The following beds are not available for the selected dates: ${unavailable.join(', ')}. Please select different beds or dates.`
      };
    }
    
    return {
      valid: true,
      conflicts: [],
      message: 'All selected beds are available'
    };
  } catch (error) {
    console.error('Error validating bed selection:', error);
    return {
      valid: false,
      conflicts: [],
      message: 'Error validating bed availability. Please try again.'
    };
  }
};

/**
 * Get available bed count for a specific date range
 * @param {string} checkInDate - Check-in date
 * @param {string} checkOutDate - Check-out date
 * @param {string} roomId - Room ID ('A' or 'B'), optional
 * @returns {Promise<number>} Number of available beds
 */
export const getAvailableBedCount = async (checkInDate, checkOutDate, roomId = null) => {
  try {
    const unavailableBeds = await getUnavailableBeds(checkInDate, checkOutDate);
    
    // Total beds: Room A (22) + Room B (16) = 38
    const totalBeds = roomId === 'A' ? 22 : roomId === 'B' ? 16 : 38;
    
    // Filter unavailable beds by room if specified
    let unavailableCount = 0;
    if (roomId) {
      unavailableCount = unavailableBeds.filter(bedId => bedId.startsWith(roomId)).length;
    } else {
      unavailableCount = unavailableBeds.length;
    }
    
    return Math.max(0, totalBeds - unavailableCount);
  } catch (error) {
    console.error('Error getting available bed count:', error);
    return roomId === 'A' ? 22 : roomId === 'B' ? 16 : 38; // Return full capacity on error
  }
};

/**
 * Check if a specific bed is available for given dates
 * @param {string} bedId - Bed ID (e.g., 'A1', 'B5')
 * @param {string} checkInDate - Check-in date
 * @param {string} checkOutDate - Check-out date
 * @returns {Promise<boolean>} True if bed is available
 */
export const isBedAvailable = async (bedId, checkInDate, checkOutDate) => {
  try {
    const unavailableBeds = await getUnavailableBeds(checkInDate, checkOutDate);
    return !unavailableBeds.includes(bedId);
  } catch (error) {
    console.error('Error checking bed availability:', error);
    return true; // Return available on error to avoid blocking
  }
};
